
import { sections as defaultSections } from '../data';
import {
  Field,
  FormConfig,
  FormData,
  SectionConfig,
  VisibilityRule,
} from '../types';

const STORAGE_KEY = 'loan-form-admin-config-v1';

/** Flat lookup of every field defined in the record. */
export const fieldCatalog: Record<string, Field> = defaultSections.reduce(
  (acc, section) => {
    section.fields.forEach((f) => {
      acc[f.id] = f;
    });
    return acc;
  },
  {} as Record<string, Field>,
);

export const allFieldIds = Object.keys(fieldCatalog);

export function buildDefaultConfig(): FormConfig {
  return {
    sections: defaultSections.map<SectionConfig>((s) => ({
      id: s.id,
      title: s.title,
      icon: s.icon,
      hidden: false,
      columns: 2,
      defaultCollapsed: false,
      fieldIds: s.fields.map((f) => f.id),
    })),
    hiddenFieldIds: [],
    rules: [
      {
        id: 'rule-coborrower',
        enabled: true,
        targetFieldId: 'coBorrowerFirstName',
        action: 'hide',
        conditionFieldId: 'isCoBorrower',
        operator: 'equals',
        conditionValue: 'No',
      },
    ],
    density: 'comfortable',
    showHeader: true,
    headerMetricIds: ['totalLoanAmount', 'interestRate', 'ltv', 'typeOfLoan'],
    showSearch: true,
  };
}

/* ---------------- persistence ---------------- */

export function loadConfig(): FormConfig {
  if (typeof window === 'undefined') return buildDefaultConfig();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return buildDefaultConfig();
    const parsed = JSON.parse(raw) as FormConfig;
    if (!parsed?.sections?.length) return buildDefaultConfig();
    return parsed;
  } catch {
    return buildDefaultConfig();
  }
}

export function saveConfig(config: FormConfig): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch {
    /* storage unavailable — config stays in memory */
  }
}

export function clearStoredConfig(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* no-op */
  }
}

/* ---------------- rule evaluation ---------------- */

function normalize(value: string | boolean | undefined): string {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value ?? '').trim();
}

export function conditionMet(rule: VisibilityRule, values: FormData): boolean {
  const actual = normalize(values[rule.conditionFieldId]);
  const expected = rule.conditionValue.trim();

  switch (rule.operator) {
    case 'equals':
      return actual.toLowerCase() === expected.toLowerCase();
    case 'notEquals':
      return actual.toLowerCase() !== expected.toLowerCase();
    case 'isEmpty':
      return actual === '';
    case 'isNotEmpty':
      return actual !== '';
    default:
      return false;
  }
}

/**
 * Resolve which fields are hidden, combining unconditional admin hides
 * with any conditional rules that currently match.
 */
export function computeHiddenFields(config: FormConfig, values: FormData): Set<string> {
  const hidden = new Set(config.hiddenFieldIds);

  config.rules.forEach((rule) => {
    if (!rule.enabled || !rule.targetFieldId || !rule.conditionFieldId) return;
    if (!conditionMet(rule, values)) return;
    if (rule.action === 'hide') hidden.add(rule.targetFieldId);
    else hidden.delete(rule.targetFieldId);
  });

  return hidden;
}

export function describeRule(rule: VisibilityRule): string {
  const target = fieldCatalog[rule.targetFieldId]?.label ?? 'Field';
  const condition = fieldCatalog[rule.conditionFieldId]?.label ?? 'field';
  const verb = rule.action === 'hide' ? 'Hide' : 'Show';

  switch (rule.operator) {
    case 'isEmpty':
      return `${verb} “${target}” when “${condition}” is empty`;
    case 'isNotEmpty':
      return `${verb} “${target}” when “${condition}” has a value`;
    case 'notEquals':
      return `${verb} “${target}” when “${condition}” is not “${rule.conditionValue || '—'}”`;
    default:
      return `${verb} “${target}” when “${condition}” is “${rule.conditionValue || '—'}”`;
  }
}

/* ---------------- immutable helpers ---------------- */

export function moveItem<T>(list: T[], index: number, direction: -1 | 1): T[] {
  const next = [...list];
  const target = index + direction;
  if (target < 0 || target >= next.length) return next;
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

export function createRuleId(): string {
  return `rule-${Math.random().toString(36).slice(2, 9)}`;
}

export function createSectionId(): string {
  return `section-${Math.random().toString(36).slice(2, 9)}`;
}

